// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Users, Wand2, AlertTriangle, Check, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatTime } from '@/lib/dates';

interface Match {
  id: string;
  round_number: number;
  match_number: number;
  bracket_type: string;
  team1_name?: string;
  team2_name?: string;
  scheduled_time?: string;
  court_field?: string;
  referee_id?: string;
  referee2_id?: string;
  status: string;
}

interface Referee {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  certification_level?: string;
}

interface Tournament {
  id: string;
  name: string;
}

interface RefereeAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: Tournament;
}

export const RefereeAssignmentDialog: React.FC<RefereeAssignmentDialogProps> = ({
  isOpen,
  onClose,
  tournament,
}) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [referees, setReferees] = useState<Referee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assignments, setAssignments] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (isOpen && tournament?.id) {
      fetchData();
    }
  }, [isOpen, tournament?.id]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [matchesResult, refereesResult] = await Promise.all([
        supabase
          .from('tournament_matches')
          .select('*')
          .eq('tournament_id', tournament.id)
          .not('scheduled_time', 'is', null)
          .order('scheduled_time'),
        supabase
          .from('tournament_referees')
          .select('*')
          .eq('tournament_id', tournament.id)
          .order('name'),
      ]);

      if (matchesResult.error) throw matchesResult.error;
      if (refereesResult.error) throw refereesResult.error;

      setMatches(matchesResult.data || []);
      setReferees(refereesResult.data || []);

      // Initialize assignments from existing data
      const initialAssignments = new Map<string, string>();
      (matchesResult.data || []).forEach((match: Match) => {
        if (match.referee_id) {
          initialAssignments.set(match.id, match.referee_id);
        }
      });
      setAssignments(initialAssignments);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const isRefereeBusy = (refereeId: string, scheduledTime?: string): boolean => {
    if (!scheduledTime) return false;
    
    const timeKey = scheduledTime.substring(0, 16); // Group by hour:minute
    
    // Check if this referee is already assigned to a match at the same time
    return matches.some(match => {
      if (!match.scheduled_time) return false;
      const matchTimeKey = match.scheduled_time.substring(0, 16);
      const assignedRef = assignments.get(match.id);
      return matchTimeKey === timeKey && assignedRef === refereeId;
    });
  };

  const handleAssignReferee = async (matchId: string, refereeId: string) => {
    try {
      const updateValue = refereeId === '' ? null : refereeId;
      
      const { error } = await supabase
        .from('tournament_matches')
        .update({ referee_id: updateValue })
        .eq('id', matchId);

      if (error) throw error;

      // Update local state
      const newAssignments = new Map(assignments);
      if (refereeId === '') {
        newAssignments.delete(matchId);
      } else {
        newAssignments.set(matchId, refereeId);
      }
      setAssignments(newAssignments);

      toast.success('Referee assigned');
    } catch (error) {
      console.error('Error assigning referee:', error);
      toast.error('Failed to assign referee');
    }
  };

  const handleAutoAssign = async () => {
    if (referees.length === 0) {
      toast.error('No referees available to assign');
      return;
    }

    setIsLoading(true);
    try {
      // Get unassigned scheduled matches sorted by time
      const unassigned = matches
        .filter(m => !assignments.get(m.id) && m.scheduled_time)
        .sort((a, b) => 
          new Date(a.scheduled_time!).getTime() - new Date(b.scheduled_time!).getTime()
        );

      if (unassigned.length === 0) {
        toast.info('All scheduled matches already have referees assigned');
        return;
      }

      // Track referee assignments by time slot
      const refAssignments = new Map<string, Set<string>>(); // timeKey -> Set of referee IDs
      const newAssignments = new Map(assignments);
      const updates: { matchId: string; refereeId: string }[] = [];

      for (const match of unassigned) {
        const timeKey = match.scheduled_time!.substring(0, 16);
        
        if (!refAssignments.has(timeKey)) {
          refAssignments.set(timeKey, new Set());
        }

        const busyRefs = refAssignments.get(timeKey)!;
        
        // Also check existing assignments from the database
        matches.forEach(m => {
          if (m.scheduled_time?.substring(0, 16) === timeKey && m.referee_id) {
            busyRefs.add(m.referee_id);
          }
        });

        // Find available referee (round-robin distribution)
        const availableRef = referees.find(ref => !busyRefs.has(ref.id));

        if (availableRef) {
          busyRefs.add(availableRef.id);
          updates.push({ matchId: match.id, refereeId: availableRef.id });
          newAssignments.set(match.id, availableRef.id);
        }
      }

      // Batch update to database
      for (const update of updates) {
        await supabase
          .from('tournament_matches')
          .update({ referee_id: update.refereeId })
          .eq('id', update.matchId);
      }

      setAssignments(newAssignments);
      toast.success(`Auto-assigned ${updates.length} referees to matches`);
      
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Error auto-assigning:', error);
      toast.error('Failed to auto-assign referees');
    } finally {
      setIsLoading(false);
    }
  };

  const assignedCount = assignments.size;
  const unassignedCount = matches.filter(m => m.scheduled_time && !assignments.get(m.id)).length;
  const scheduledCount = matches.filter(m => m.scheduled_time).length;

  const formatMatchTime = (time?: string) => {
    if (!time) return 'Not scheduled';
    return `${format(new Date(time), 'MMM d')}, ${formatTime(time)}`;
  };

  const getRefereeNameById = (refereeId?: string) => {
    if (!refereeId) return null;
    return referees.find(r => r.id === refereeId)?.name;
  };

  // Group matches by round
  const matchesByRound = matches
    .filter(m => m.scheduled_time)
    .reduce((acc, match) => {
      const round = match.round_number;
      if (!acc[round]) acc[round] = [];
      acc[round].push(match);
      return acc;
    }, {} as Record<number, Match[]>);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Assign Referees - {tournament.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="flex flex-wrap gap-4 text-sm">
            <Badge variant="outline" className="px-3 py-1">
              Total Scheduled: {scheduledCount}
            </Badge>
            <Badge variant="default" className="px-3 py-1 bg-green-600">
              <Check className="h-3 w-3 mr-1" />
              Assigned: {assignedCount}
            </Badge>
            <Badge variant="secondary" className="px-3 py-1">
              Unassigned: {unassignedCount}
            </Badge>
            <Badge variant="outline" className="px-3 py-1">
              Referees Available: {referees.length}
            </Badge>
          </div>

          {/* Warning if no referees */}
          {referees.length === 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No referees have been added to this tournament. Please add referees in the Referees tab first.
              </AlertDescription>
            </Alert>
          )}

          {/* Auto-assign button */}
          <div className="flex gap-2">
            <Button
              onClick={handleAutoAssign}
              disabled={isLoading || referees.length === 0 || unassignedCount === 0}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Wand2 className="h-4 w-4" />
              Auto-Assign Referees
            </Button>
          </div>

          {/* Match list grouped by round */}
          <div className="space-y-4">
            {Object.entries(matchesByRound)
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([round, roundMatches]) => (
                <Card key={round}>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">Round {round}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {roundMatches.map((match) => {
                      const currentAssignment = assignments.get(match.id);
                      
                      return (
                        <div
                          key={match.id}
                          className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {match.team1_name || 'TBD'} vs {match.team2_name || 'TBD'}
                            </div>
                            <div className="text-sm text-muted-foreground flex flex-wrap gap-2 mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatMatchTime(match.scheduled_time)}
                              </span>
                              {match.court_field && (
                                <span>• {match.court_field}</span>
                              )}
                              <span>• Match #{match.match_number}</span>
                            </div>
                          </div>
                          
                          <div className="w-full sm:w-56">
                            <Select
                              value={currentAssignment || ''}
                              onValueChange={(value) => handleAssignReferee(match.id, value)}
                              disabled={isLoading}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select referee" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="">Unassigned</SelectItem>
                                {referees.map((ref) => {
                                  const isBusy = currentAssignment !== ref.id && 
                                    isRefereeBusy(ref.id, match.scheduled_time);
                                  
                                  return (
                                    <SelectItem
                                      key={ref.id}
                                      value={ref.id}
                                      disabled={isBusy}
                                    >
                                      {ref.name}
                                      {ref.certification_level && ` (${ref.certification_level})`}
                                      {isBusy && ' - Busy'}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}
          </div>

          {scheduledCount === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No scheduled matches found.</p>
              <p className="text-sm">Schedule matches first before assigning referees.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RefereeAssignmentDialog;
